import * as React from 'react';
import {
    Typography,
    MenuItem,
    CircularProgress,
    Chip,
    Box,
} from '@mui/material';
import { apiService } from '../../services/api';
import { getEnvironmentConfig } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FormContainer,
    FormTitle,
    FormSection,
    SectionTitle,
    FormRow,
    FormField,
    ButtonContainer,
    SubmitButton,
    ResetButton,
} from './ChargeForm.styles';
import { useToast } from '../../providers/ToastContext';
import { useForm, Controller } from 'react-hook-form';
import { ChargeById, ChargeCodeItem } from '../../types';

interface ChargeFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
    mode?: 'create' | 'edit';
    chargeData?: ChargeById | null;
    isModal?: boolean;
}

type FormData = {
    name: string;
    value: number | string;
    status: 'active' | 'inactive';
    chargeCode: string;
    merchantId: string;
};

const ChargesForm: React.FC<ChargeFormProps> = ({
    onSuccess,
    onCancel,
    mode = 'create',
    chargeData = null,
    isModal = false,
}) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const config = getEnvironmentConfig();
    const endpoint =
        mode === 'create'
            ? `${config.API_BASE_URL}/charges/create`
            : `${config.API_BASE_URL}/charges/update`;
    const { id } = useParams<{ id: string }>();

    // State for charge codes
    const [chargeCodes, setChargeCodes] = React.useState<ChargeCodeItem[]>([]);
    const [loadingChargeCodes, setLoadingChargeCodes] = React.useState(false);

    // Fetch charge codes on component mount
    React.useEffect(() => {
        const fetchCodes = async () => {
            try {
                setLoadingChargeCodes(true);
                const response = await apiService.getChargeCodes();
                setChargeCodes(response.data);
            } catch (error) {
                showToast('Failed to fetch charge codes', 'error');
            } finally {
                setLoadingChargeCodes(false);
            }
        };
        fetchCodes();
    }, [showToast]);

    // Initialize React Hook Form with conditional defaults
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({
        defaultValues: {
            name: chargeData?.name || '',
            value: chargeData?.value || '',
            status: chargeData?.status || 'active',
            chargeCode: chargeData?.chargeCode || '',
            merchantId: chargeData?.merchantId?.toString() || id || '',
        },
    });

    // Reset form when chargeData changes (for edit mode)
    React.useEffect(() => {
        if (chargeData && mode === 'edit') {
            reset({
                name: chargeData.name || '',
                value: chargeData.value || '',
                status: chargeData.status || 'active',
                chargeCode: chargeData.chargeCode || '',
                merchantId: chargeData.merchantId?.toString() || id || '',
            });
        }
    }, [chargeData, mode, reset, id]);

    // Form submit handler
    const onSubmit = async (data: FormData) => {
        try {
            const payload = {
                ...data,
                value: Number(data.value),
            };

            let response;
            console.log(mode, 'mode is');

            if (mode === 'create') {
                response = await apiService.createCharge(payload);
            } else {
                const updatePayload = {
                    name: payload.name,
                    value: payload.value,
                    status: payload.status,
                    chargeCode: payload.chargeCode,
                };
                response = await apiService.updateCharge(
                    updatePayload,
                    chargeData!.id!
                );
            }

            console.log(response);

            if (response?.data || (response as any)?.result) {
                const successMessage =
                    mode === 'create'
                        ? 'Charge created successfully!'
                        : 'Charge updated successfully!';

                showToast(successMessage, 'success');

                if (mode === 'create') {
                    reset({
                        name: '',
                        chargeCode: '',
                        value: '',
                        status: 'active',
                        merchantId: id || '',
                    });
                }

                onSuccess?.();

                if (!isModal && mode === 'create') {
                    setTimeout(() => navigate('/merchants'), 2000);
                }
            } else {
                showToast(`Failed to ${mode} charge`, 'error');
            }
        } catch (err) {
            console.error('Error in onSubmit:', err);
            showToast(
                'An unexpected error occurred. Please try again.',
                'error'
            );
        }
    };

    const handleReset = () => {
        if (mode === 'edit' && chargeData) {
            reset({
                name: chargeData.name || '',
                value: chargeData.value || '',
                status: chargeData.status || 'active',
                chargeCode: chargeData.chargeCode || '',
                merchantId: chargeData.merchantId?.toString() || id || '',
            });
        } else {
            reset({
                name: '',
                chargeCode: '',
                value: '',
                status: 'active',
                merchantId: id || '',
            });
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else if (!isModal) {
            navigate('/merchants');
        }
    };

    const getTitle = () => {
        return mode === 'create' ? 'Create New Charge' : 'Edit Charge';
    };

    const getSubmitButtonText = () => {
        return isSubmitting ? (
            <CircularProgress size={24} />
        ) : mode === 'create' ? (
            'Create Charge'
        ) : (
            'Update Charge'
        );
    };

    return (
        <FormContainer
            elevation={isModal ? 0 : 3}
            sx={isModal ? { boxShadow: 'none', p: 0 } : {}}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormTitle sx={isModal ? { mb: 3 } : {}}>
                    <Typography
                        variant={isModal ? 'h5' : 'h4'}
                        component="h1"
                        gutterBottom
                    >
                        {getTitle()}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            justifyContent: 'center',
                            mt: isModal ? 0 : 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            API Endpoint:
                        </Typography>
                        <Chip
                            label={`${mode === 'create' ? 'POST' : 'PUT'} ${endpoint}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                </FormTitle>

                {/* Charge Fields Section */}
                <FormSection>
                    <SectionTitle>
                        <Typography variant="h6">Charge Information</Typography>
                    </SectionTitle>
                    <FormRow>
                        {/* Charge Name */}
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: 'Charge Name is required' }}
                            render={({ field }) => (
                                <FormField
                                    label="Charge Name"
                                    {...field}
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />

                        {/* Charge Code - Changed to Dropdown */}
                        <Controller
                            name="chargeCode"
                            control={control}
                            rules={{ required: 'Charge Code is required' }}
                            render={({ field }) => (
                                <FormField
                                    select
                                    label="Charge Code"
                                    {...field}
                                    fullWidth
                                    error={!!errors.chargeCode}
                                    helperText={errors.chargeCode?.message}
                                    disabled={loadingChargeCodes}
                                >
                                    {loadingChargeCodes ? (
                                        <MenuItem disabled>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                <CircularProgress size={16} />
                                                Loading charge codes...
                                            </Box>
                                        </MenuItem>
                                    ) : chargeCodes.length === 0 ? (
                                        <MenuItem disabled>
                                            No charge codes available
                                        </MenuItem>
                                    ) : (
                                        chargeCodes.map(chargeCode => (
                                            <MenuItem
                                                key={chargeCode.id}
                                                value={chargeCode.code}
                                            >
                                                <Box>
                                                    <Typography variant="body2">
                                                        {chargeCode.name}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))
                                    )}
                                </FormField>
                            )}
                        />
                    </FormRow>

                    <FormRow>
                        {/* Value */}
                        <Controller
                            name="value"
                            control={control}
                            rules={{
                                required: 'Value is required',
                                min: { value: 0, message: 'Value must be ≥ 0' },
                            }}
                            render={({ field }) => (
                                <FormField
                                    type="number"
                                    label="Value"
                                    placeholder="0"
                                    {...field}
                                    fullWidth
                                    error={!!errors.value}
                                    helperText={errors.value?.message}
                                />
                            )}
                        />

                        {/* Status */}
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <FormField
                                    select
                                    label="Status"
                                    {...field}
                                    fullWidth
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">
                                        Inactive
                                    </MenuItem>
                                </FormField>
                            )}
                        />

                        {/* Merchant ID (readonly) */}
                        <Controller
                            name="merchantId"
                            control={control}
                            render={({ field }) => (
                                <FormField
                                    label="Merchant ID"
                                    {...field}
                                    type="text"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                            )}
                        />
                    </FormRow>
                </FormSection>

                {/* Action Buttons */}
                <ButtonContainer sx={isModal ? { mt: 3 } : {}}>
                    {mode === 'create' && (
                        <ResetButton
                            variant="outlined"
                            onClick={handleReset}
                            disabled={isSubmitting}
                        >
                            Reset
                        </ResetButton>
                    )}
                    <ResetButton
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </ResetButton>
                    <SubmitButton
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || loadingChargeCodes}
                    >
                        {getSubmitButtonText()}
                    </SubmitButton>
                </ButtonContainer>
            </form>
        </FormContainer>
    );
};

export default ChargesForm;
